/** Measures app chrome only; headers inside a designer preview are not navigation. */
export class LoaderPosition {
  private frame = 0;
  private resize?: ResizeObserver;
  private mutation?: MutationObserver;
  private observed = new Set<Element>();
  private win: Window;
  private schedule = () => {
    if (!this.frame) this.frame = this.win.requestAnimationFrame(() => {
      this.frame = 0;
      this.measure();
    });
  };

  constructor(private host: HTMLElement) { this.win = host.ownerDocument.defaultView!; }

  start(): void {
    this.resize = new ResizeObserver(this.schedule);
    this.mutation = new MutationObserver(records => {
      if (records.some(record => {
        const element = record.target.nodeType === 1 ? record.target as Element : record.target.parentElement;
        return !element?.closest('app-loading-spinner');
      })) this.schedule();
    });
    this.mutation.observe(this.host.ownerDocument.documentElement, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'data-navigation-ready']
    });
    this.win.addEventListener('resize', this.schedule);
    this.win.addEventListener('scroll', this.schedule, { passive: true });
    this.win.visualViewport?.addEventListener('resize', this.schedule);
    this.win.visualViewport?.addEventListener('scroll', this.schedule);
    this.measure();
  }

  stop(): void {
    this.resize?.disconnect();
    this.mutation?.disconnect();
    this.win.cancelAnimationFrame(this.frame);
    this.win.removeEventListener('resize', this.schedule);
    this.win.removeEventListener('scroll', this.schedule);
    this.win.visualViewport?.removeEventListener('resize', this.schedule);
    this.win.visualViewport?.removeEventListener('scroll', this.schedule);
    this.observed.clear();
  }

  private measure(): void {
    const doc = this.host.ownerDocument;
    const viewport = this.win.visualViewport;
    const vx = viewport?.offsetLeft || 0, vy = viewport?.offsetTop || 0;
    const vw = viewport?.width || doc.documentElement.clientWidth;
    const vh = viewport?.height || this.win.innerHeight;
    let left = vx, right = vx + vw, top = vy, bottom = vy + vh;
    const shell = this.host.closest('.church-shell');
    const content = shell?.querySelector<HTMLElement>(':scope > .content');
    const compact = shell?.querySelector<HTMLElement>(':scope > .header .church-header');
    const desktop = shell?.querySelector<HTMLElement>(':scope > app-cms-desktop-shell');
    const footer = shell?.querySelector<HTMLElement>(':scope > app-footer .church-footer');
    const targets = [doc.documentElement, shell, content, compact, desktop, footer].filter(Boolean) as Element[];
    for (const target of this.observed) if (!targets.includes(target)) {
      this.resize?.unobserve(target); this.observed.delete(target);
    }
    for (const target of targets) if (!this.observed.has(target)) {
      this.resize?.observe(target); this.observed.add(target);
    }
    const rect = (element?: Element | null, horizontalOnly = false) => {
      if (!element || !element.getClientRects().length || this.win.getComputedStyle(element).visibility === 'hidden') return null;
      const bounds = element.getBoundingClientRect();
      return bounds.width > 0 && (horizontalOnly || bounds.height > 0) ? bounds : null;
    };
    const column = rect(content, true);
    if (column && column.right > left && column.left < right) {
      left = Math.max(left, column.left); right = Math.min(right, column.right);
    }
    const header = rect(compact);
    const website = rect(desktop);
    const side = ['left', 'right'].includes(shell?.getAttribute('data-desktop-position') || '');
    if (website && side) {
      if (website.left <= vx + 1) left = Math.max(left, website.right);
      else if (website.right >= vx + vw - 1) right = Math.min(right, website.left);
    }
    const websiteHeader = desktop?.querySelector<HTMLElement>('.cms-site-header');
    const activeHeader = header ? compact : website ? websiteHeader : null;
    const headerBounds = header || (!side ? website : null);
    if (headerBounds && headerBounds.bottom > top && headerBounds.top <= top + 1) top = Math.min(bottom, headerBounds.bottom);
    const menu = rect(footer);
    if (menu) {
      if (menu.height > menu.width && menu.width < vw / 2) {
        if (menu.left <= vx + 1) left = Math.max(left, menu.right);
        else if (menu.right >= vx + vw - 1) right = Math.min(right, menu.left);
      } else if (menu.bottom >= bottom - 1 && menu.top > top) bottom = menu.top;
    }
    // A row of branding and account controls gives a stable visual center.
    // Stacked website headers and side menus use the main content column instead.
    const logo = rect(activeHeader?.querySelector('.church-logo, .cms-identity-logo, .cms-identity-symbol'));
    const avatar = rect(activeHeader?.querySelector('.account-avatar-image, .cms-account-control'));
    let center = (left + right) / 2;
    if (logo && avatar && Math.abs((logo.top + logo.height / 2) - (avatar.top + avatar.height / 2)) < Math.max(logo.height, avatar.height) / 2) {
      const anchor = (logo.left + logo.width / 2 + avatar.left + avatar.width / 2) / 2;
      if (anchor > left + 72 && anchor < right - 72) center = anchor;
    }
    // Keep the recovery panel and localized loading label inside the same column.
    const width = Math.max(0, 2 * Math.min(center - left, right - center));
    const styles = { left: center - width / 2, top, width, height: Math.max(0, bottom - top) };
    for (const [property, value] of Object.entries(styles)) {
      const next = `${value}px`;
      if (this.host.style.getPropertyValue(property) !== next) this.host.style.setProperty(property, next);
    }
    const desktopPending = this.win.matchMedia('(min-width:1024px) and (hover:hover) and (pointer:fine)').matches
      && desktop?.getAttribute('data-navigation-ready') === 'false';
    // Never paint a guessed spinner position while the app/website menu initializes.
    // Keep accessible progress feedback until its final navigation geometry exists.
    this.host.classList.toggle('loading-awaiting-navigation', !shell || desktopPending || !(header || rect(websiteHeader)));
  }
}
